import React, { useState, useEffect, useRef } from "react";
import {
  Shop,
  ClockHistory,
  Search,
  BoxSeam,
  Phone,
  GeoAlt,
  Lock,
  ShieldCheck,
  ThreeDotsVertical,
  Trash2,
  Wallet2,
} from "react-bootstrap-icons";
import Utils from "../../utils";
import { decryptData } from "../../utils/crypto";
import {
  getAllCustomerConfigList,
  getCustomerStoreList,
  removeCustomerStorePin,
} from "../../services/customerServices";
import { useTranslation } from "react-i18next";
import FlexibleSkeleton from "../../components/Common/FlexibleSkeleton";
import { formatCurrencyINR, formatTime } from "../../utils/function";
import { useNavigate } from "react-router-dom";
import { notify } from "../../components/Common/Alerts";
import config from "../../config";

const StoreList = ({
  onSelectStore,
  selectedProductStore,
  setPinModelOpen,
  setPinData,
  setUpdateModel,
  setShowCatalogue,
  setIsModalDeleteOpen,
  isModalDeleteOpen,
  setIsModalCurrentPinDeleteOpen,
  isModalCurrentPinOpen,
}) => {
  const { t } = useTranslation();
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  const [selectedStoreId, setSelectedStoreId] = useState(
    selectedProductStore?.id || null,
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [configList, setConfigList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [formData, setFormData] = useState({
    is_yours_store: 0,
  });
  const [openMenuStoreId, setOpenMenuStoreId] = useState(null);
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [deleteData, setDeleteData] = useState("");
  const [loadingPage, setLoadingPage] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [pin, setPin] = useState(["", "", "", ""]);

  const handleOpenMap = (store) => {
    if (!store?.latitude || !store?.longitude) return;

    const destination = `${store.latitude},${store.longitude}`;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const origin = `${position.coords.latitude},${position.coords.longitude}`;

        const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;

        window.open(url, "_blank");
      },
      () => {
        const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
        window.open(url, "_blank");
      },
    );
  };

  const handlePinModelOpen = (e, store) => {
    setPinModelOpen(true);
    setPinData(store);
    setOpenMenuStoreId(null);
    // setShowCatalogue(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    getAllCustomerConfigList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const backendCategories = decrypted.data.business_type || [];
        const categories = [
          { label: "All", value: "all" },
          ...backendCategories,
        ];
        setConfigList({ business_type: categories });
        setActiveCategory({ label: "All", value: "all" });
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        console.log("Location denied", err);
      },
    );
  }, []);

  const fetchStores = async () => {
    try {
      if (page === 1) {
        setLoading(true); // full page loader
      } else {
        setLoadingPage(true); // pagination loader
      }

      const data = {
        lat: formData.is_yours_store === 0 ? userLocation?.lat || "" : "",
        lng: formData.is_yours_store === 0 ? userLocation?.lng || "" : "",
        search: debouncedSearch || "",
        business_type:
          activeCategory.value === "all" ? "" : activeCategory.value,
        is_yours_store: formData ? formData?.is_yours_store : "",
        page: page,
        limit: config.DATA_LIMIT,
      };

      const response = await getCustomerStoreList(data);
      const decrypted = decryptData(response.data.data);

      const newData = decrypted.data || [];

      setStoreList((prev) => (page === 1 ? newData : [...prev, ...newData]));

      if (newData.length < config.DATA_LIMIT) {
        setHasMore(false);
      }
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setLoading(false);
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, [debouncedSearch, activeCategory, page, formData]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [searchQuery, activeCategory]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollHeight - scrollTop <= clientHeight + 50 && // near bottom
      !loading &&
      hasMore
    ) {
      setPage((prev) => prev + 1);
      setLoadingPage(true);
    }
  };

  const handleReset = () => {
    setSearchQuery("");
    setActiveCategory({ label: "All", value: "all" });
    setPage(1);
    setHasMore(true);
    setStoreList([]);
    setLoadingPage(false);
  };

  const handleRemovePin = async () => {
    const enteredPin = pin.join("");
    const payload = {
      store_id: deleteData.id,
      current_pin: enteredPin,
    };

    try {
      setBtnDeleteLoading(true);
      const response = await removeCustomerStorePin(payload);
      const decrypted = decryptData(response.data.data);
      fetchStores();

      notify.success(decrypted.message);
      setUpdateModel(false);
      setIsModalDeleteOpen(false);
      setShowCatalogue(false);
      setPinModelOpen(false);
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnDeleteLoading(false);
    }
  };

  const handleInputChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value.substring(0, 1);
    setPin(newPin);

    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleCurrentPin = () => {
    const enteredPin = pin.join("");

    if (!enteredPin) {
      notify.error(t("enterYourCompleteCurrentPIN"));

      return;
    }
    if (enteredPin === String(deleteData.pin)) {
      setIsModalDeleteOpen(true);
      setIsModalCurrentPinDeleteOpen(false);
    } else {
      notify.error("Invalid PIN ❌");
      setPin(["", "", "", ""]);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#FCFCFC] dark:bg-slate-950 font-sans overflow-hidden transition-colors duration-300">
      {/* Search Bar Section */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white hidden md:flex">
            {t("selectStore")}
          </h1>

          {/* Switch Toggle Section */}
          <div className="flex items-center gap-4 ml-auto">
            <span
              className={`text-sm font-semibold transition ${
                formData.is_yours_store === 1
                  ? "text-slate-800 dark:text-slate-200"
                  : "text-slate-400"
              }`}
            >
              {t("nearbyStores")}
            </span>

            <label className="relative inline-flex items-center cursor-pointer group">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.is_yours_store === 1}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    is_yours_store: e.target.checked ? 1 : 0,
                  }))
                }
              />
              <div
                className="w-16 h-8 bg-slate-200 dark:bg-slate-800 rounded-full peer 
            peer-focus:ring-4 peer-focus:ring-orange-100 dark:peer-focus:ring-orange-900/20
            peer-checked:bg-[#E67E22] 
            after:content-[''] after:absolute after:top-[4px] after:left-[4px] 
            after:bg-white after:rounded-full after:h-6 after:w-6 
            after:transition-all after:shadow-md
            peer-checked:after:translate-x-full
            transition-all duration-300 ease-in-out"
              ></div>
            </label>

            <span
              className={`text-sm font-semibold transition ${
                formData.is_yours_store === 0
                  ? "text-slate-800 dark:text-slate-200"
                  : "text-slate-400"
              }`}
            >
              {t("allStores")}
            </span>
          </div>
        </div>

        <div className="relative w-full mb-5">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Search stores..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-xl py-3.5 pl-12 pr-10 text-sm outline-none focus:ring-2 focus:ring-orange-500/20 text-slate-800 dark:text-slate-100 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-nowrap md:flex-wrap gap-3 overflow-x-auto md:overflow-x-visible w-full px-4 md:px-6 py-2 no-scrollbar">
        {configList?.business_type
          ?.slice()
          .sort((a, b) => (a.value === "all" ? -1 : b.value === "all" ? 1 : 0))
          .map((cat) => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 px-6 md:px-8 py-2.5 rounded-xl font-bold text-sm transition-all border ${
                activeCategory.value === cat.value
                  ? "bg-[#E67E22] text-white border-[#E67E22] shadow-sm"
                  : "bg-white dark:bg-slate-900 text-slate-400 border-slate-200 dark:border-slate-800"
              }`}
            >
              {cat.label}
            </button>
          ))}
      </div>

      {/* Main Content Area */}
      <main
        // className={
        //   storeList?.length >= 1 && storeList?.length <= 2
        //     ? "min-h-0 h-auto p-4"
        //     : "flex-1 overflow-y-auto p-6 space-y-4 pb-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:min-h-0 h-auto p-4"
        // }
        className="flex-1 overflow-y-auto p-6 scroll-smooth pb-32"
        onScroll={handleScroll}
        // onScroll={handleScroll}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <FlexibleSkeleton count={10} />
          ) : storeList?.length > 0 ? (
            storeList?.map((store) => (
              <div
                key={store.id}
                className={`group relative bg-white dark:bg-slate-900 rounded-[2rem] p-6 border-2 transition-all cursor-pointer ${
                  selectedStoreId === store.id
                    ? "border-[#E67E22] shadow-2xl shadow-orange-100 dark:shadow-none"
                    : "border-slate-100 dark:border-slate-800 shadow-sm hover:border-orange-200 dark:hover:border-slate-700"
                }`}
                onClick={() => {
                  setSelectedStoreId(store.id);
                  onSelectStore(store);

                  if (window.innerWidth < 1024) {
                    setIsMobilePanelOpen(true);
                    setSelectedStoreId(store.id);
                    onSelectStore(store);
                  }

                  if (store.pin) {
                    setPinData(store);
                    setPinModelOpen(true);
                  }
                  if (openMenuStoreId === store.id) {
                    setShowCatalogue(false);
                    setPinModelOpen(false);
                  }
                }}
              >
                {/* Options Menu */}
                <div className="absolute top-0 right-4 z-20 cursor-ponter">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuStoreId(
                        openMenuStoreId === store.id ? null : store.id,
                      );
                    }}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400"
                  >
                    <ThreeDotsVertical size={20} />
                  </button>

                  {openMenuStoreId === store.id && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuStoreId(null);
                        }}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-xl rounded-2xl z-30 overflow-hidden animate-swal-in">
                        <div className="p-1.5 space-y-1">
                          {!store.pin ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePinModelOpen(e, store);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-[#E67E22] rounded-xl transition-all"
                            >
                              <Lock size={16} /> {t("setPin")}
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setUpdateModel(true);
                                setPinModelOpen(true);
                                setPinData(store);
                                setOpenMenuStoreId(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-[#E67E22] rounded-xl transition-all"
                            >
                              <ShieldCheck size={16} /> {t("updatePin")}
                            </button>
                          )}
                          <div className="h-[1px] bg-slate-100 dark:bg-slate-700 mx-2 my-1" />
                          {store.pin && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsModalCurrentPinDeleteOpen(true);
                                setOpenMenuStoreId(null);
                                setDeleteData(store);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                            >
                              <Trash2 size={16} /> {t("removePIN")}
                            </button>
                          )}

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/wallet-store-details/${store.id}`);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-[#E67E22] rounded-xl transition-all"
                          >
                            <Wallet2
                              size={18}
                              className="group-hover:scale-110 transition-transform"
                            />
                            {/* Separator */}
                            <span className="font-bold tracking-wide">
                              {t("wallet")}{" "}
                              ({formatCurrencyINR(store.wallet_balance)})
                            </span>{" "}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-between items-start mb-6 mt-3">
                  <div className="flex-1 min-w-0">
                    {/* Header Section: Name + Status Badge */}
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h3 className="font-black text-xl md:text-2xl text-slate-800 dark:text-white leading-tight break-words">
                        {store.name}
                      </h3>

                      <div
                        className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-[0.1em] border shadow-sm ${
                          store.online_order === 1
                            ? "bg-emerald-500 text-white border-emerald-600"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-400 border-slate-200 dark:border-slate-700"
                        }`}
                      >
                        {store.online_order === 0
                          ? t("unavailable")
                          : t("online")}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      {/* Distance Row */}
                      {store.distance && (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 dark:bg-orange-500/10 rounded-lg border border-orange-100 dark:border-orange-900/30">
                            <GeoAlt
                              size={12}
                              className="text-orange-600 dark:text-orange-400"
                              weight="fill"
                            />
                            <span className="text-[11px] font-black text-orange-700 dark:text-orange-300 uppercase">
                              {store.distance} {store.distance_unit} {t("away")}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Address Row */}
                      {store.store_address && (
                        <div className="flex items-start gap-2 group cursor-default">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenMap(store);
                            }}
                            className="mt-0.5 shrink-0 text-slate-400 group-hover:text-orange-500 transition-colors cursor-pointer"
                          >
                            <GeoAlt size={14} />
                          </div>
                          <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
                            {store.store_address}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="w-14 h-14 md:w-16 md:h-16 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center transition-colors group-hover:bg-orange-50 dark:group-hover:bg-orange-900/20 overflow-hidden">
                    {store?.store_image ? (
                      <img
                        src={store.store_image}
                        alt={store.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Shop
                        size={28}
                        className={
                          store.status === "Open"
                            ? "text-[#E67E22]"
                            : "text-slate-300 dark:text-slate-600"
                        }
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {store.order_start_time && (
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-slate-100 dark:bg-slate-800 rounded-xl text-slate-500 dark:text-slate-400">
                        <ClockHistory size={18} />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">
                          {t("onlineOrderHours")}
                        </p>
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                          {formatTime(store.order_start_time)} -{" "}
                          {formatTime(store.order_end_time)}
                        </p>
                      </div>
                    </div>
                  )}
                  {store.mobile && (
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-slate-100 dark:bg-slate-800 rounded-xl text-slate-500 dark:text-slate-400">
                        <Phone size={18} />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">
                          {t("supportContact")}
                        </p>
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300">
                          {store.mobile}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center  w-full text-center px-6 my-auto mt-10">
              <BoxSeam
                size={48}
                className="text-slate-300 dark:text-slate-700 mb-4"
              />
              <h3 className="text-lg font-bold text-slate-700 dark:text-white mb-2">
                {searchQuery || activeCategory.value
                  ? t("noStoresFound")
                  : t("noStoresAvailable")}
              </h3>
              <p className="text-sm text-slate-400 dark:text-slate-500 mb-5">
                {searchQuery || activeCategory.value
                  ? t("tryAdjustingFilter")
                  : t("checkBackLater")}
              </p>
              <button
                onClick={handleReset}
                className="px-8 py-3 bg-[#E67E22] text-white rounded-2xl font-black text-sm shadow-lg shadow-orange-500/20 active:scale-95 transition-transform"
              >
                {t("refresh")}
              </button>
            </div>
          )}
        </div>

        {loadingPage && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
          </div>
        )}
      </main>

      {isModalCurrentPinOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-[2rem] max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {t("EnterYourCurrentPin")}
            </h3>

            <div className="flex gap-4 justify-center mb-12">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  placeholder="•"
                  onChange={(e) => handleInputChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-16 h-20 lg:w-20 lg:h-24 text-center text-4xl font-black text-gray-900 dark:text-white placeholder:text-gray-100 dark:placeholder:text-gray-800 bg-gray-50 dark:bg-[#0a0f1a] border-2 border-gray-100 dark:border-[#232d3f] rounded-[1.5rem] focus:border-orange-500 focus:ring-[6px] focus:ring-orange-500/10 outline-none transition-all shadow-inner"
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setIsModalCurrentPinDeleteOpen(false)}
                className="flex-1 px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleCurrentPin}
                className="flex-1 px-4 py-2 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
              >
                {t("yesRemove")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-[2rem] max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {t("removePIN")}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
              {t("remoePinDescription")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setIsModalDeleteOpen(false)}
                className="flex-1 px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleRemovePin}
                className="flex-1 px-4 py-2 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
              >
                {t("yesRemove")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreList;
